import React, { useState, useEffect } from "react";
import { Cookie, X, Settings, Check } from "lucide-react";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('fonte-juvence-cookies');
    if (!consent) {
      setTimeout(() => setShowBanner(true), 2000);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('fonte-juvence-cookies', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(essentialOnly);
    localStorage.setItem('fonte-juvence-cookies', JSON.stringify(essentialOnly));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('fonte-juvence-cookies', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (type) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gold-warm shadow-2xl z-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start space-x-4">
            <div className="bg-gold-warm/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
              <Cookie className="w-6 h-6 text-gold-warm" />
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-sage-dark mb-2">
                Utilizamos Cookies
              </h3>
              <p className="text-sage-medium text-sm leading-relaxed mb-4">
                Este website utiliza cookies para melhorar a sua experiência de navegação, 
                analisar o tráfego do site e personalizar conteúdo. Pode gerir as suas 
                preferências de cookies a qualquer momento.
              </p>
              
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="bg-gold-warm text-white px-6 py-2 rounded-full hover:bg-gold-deep transition-colors font-semibold"
                >
                  Aceitar Todos
                </button>
                <button
                  onClick={handleRejectAll}
                  className="bg-sage-soft text-sage-dark px-6 py-2 rounded-full hover:bg-sage-medium hover:text-white transition-colors"
                >
                  Rejeitar Todos
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="bg-white text-sage-dark border border-sage-soft px-6 py-2 rounded-full hover:bg-sage-soft transition-colors flex items-center space-x-2"
                >
                  <Settings className="w-4 h-4" />
                  <span>Personalizar</span>
                </button>
              </div>
            </div>
            
            <button
              onClick={() => setShowBanner(false)}
              className="text-sage-medium hover:text-sage-dark transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-playfair font-bold text-sage-dark">
                  Preferências de Cookies
                </h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-sage-medium hover:text-sage-dark transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Essential Cookies */}
                <div className="border border-sage-soft/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-sage-dark">Cookies Essenciais</h3>
                    <div className="bg-green-500 w-6 h-6 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-sage-medium text-sm">
                    Estes cookies são necessários para o funcionamento básico do website e não podem ser desactivados.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-sage-soft/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-sage-dark">Cookies de Análise</h3>
                    <button
                      onClick={() => handlePreferenceChange('analytics')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-gold-warm' : 'bg-sage-soft'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                        preferences.analytics ? 'translate-x-6' : 'translate-x-0.5'
                      }`}></div>
                    </button>
                  </div>
                  <p className="text-sage-medium text-sm">
                    Ajudam-nos a entender como os visitantes interagem com o website, fornecendo informações sobre métricas de tráfego.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-sage-soft/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-sage-dark">Cookies de Marketing</h3>
                    <button
                      onClick={() => handlePreferenceChange('marketing')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.marketing ? 'bg-gold-warm' : 'bg-sage-soft'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                        preferences.marketing ? 'translate-x-6' : 'translate-x-0.5'
                      }`}></div>
                    </button>
                  </div>
                  <p className="text-sage-medium text-sm">
                    Utilizados para mostrar anúncios relevantes e medir a eficácia das campanhas publicitárias.
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="border border-sage-soft/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-sage-dark">Cookies Funcionais</h3>
                    <button
                      onClick={() => handlePreferenceChange('functional')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.functional ? 'bg-gold-warm' : 'bg-sage-soft'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                        preferences.functional ? 'translate-x-6' : 'translate-x-0.5'
                      }`}></div>
                    </button>
                  </div>
                  <p className="text-sage-medium text-sm">
                    Permitem funcionalidades melhoradas e personalização, como lembrança das suas preferências.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-gold-warm text-white py-3 rounded-full hover:bg-gold-deep transition-colors font-semibold"
                >
                  Guardar Preferências
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-sage-dark text-white py-3 rounded-full hover:bg-sage-medium transition-colors font-semibold"
                >
                  Aceitar Todos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;